// Panel Process — gestionnaire de processus façon Process Hacker.
//
// Flux temps réel (anti-saturation WS) :
//   1. au montage : tool process_watch_start { intervalMs } → { jobId }
//   2. le plugin streame via envelope live/process/watch.* :
//        watch.snapshot { procs:[…] }              ← une seule fois, complet
//        watch.delta { add:[…], upd:[…], rm:[] }   ← à chaque tick, minimal
//   3. au démontage : process_watch_stop (le plugin arrête aussi tout seul).
//
// Les champs statiques (nom, chemin, user, cmdline, type) n'arrivent qu'une
// fois ; seuls les champs dynamiques d'un process qui a bougé sont renvoyés.
// L'arbre, les couleurs, les sparklines et le graphe global sont reconstruits
// côté client à partir de cet état.

import { createEffect, createSignal, createMemo, onCleanup, onMount, For, Show, type Component } from "solid-js";
import { PanelLayout, type PanelStatus } from "@/shared/panel-base";
import { useToolClient } from "@/shared/tool-client";
import { useWs } from "@/shared/ws";
import { isEnvelope } from "@/shared/ws-protocol";
import { useConfirm } from "@/shared/confirm-dialog";
import { formatBytes, formatRelative } from "@/shared/format-utils";
import { registerPanel } from "@/panel-registry";
import { ContextMenu, createContextMenu, type ContextMenuItem } from "@/components/context-menu";
import { Skeleton } from "@/components/skeleton";
import { RefreshIndicator } from "@/shared/panel-primitives";
import { usePanelRefresh } from "@/shared/use-panel-refresh";
import { PanelChrome } from "../console/ui";
import "./process.css";

const HIST_LEN = 32; // points d'historique par process (sparkline)
const GLOBAL_HIST = 60; // points du graphe global

interface Proc {
  pid: number;
  ppid: number | null;
  name: string;
  path: string | null;
  user: string | null;
  desc: string | null;
  type: string; // system | service | user
  start: string | null;
  cmd: string | null;
  cpu: number;
  mem: number;
  handles: number;
  threads: number;
  ioR: number;
  ioW: number;
  suspended: boolean;
  cpuHist: number[];
  memHist: number[];
}

interface RawProc {
  p: number;
  pp?: number | null;
  n?: string;
  path?: string | null;
  u?: string | null;
  d?: string | null;
  ty?: string;
  st?: string | null;
  cmd?: string | null;
  cpu?: number;
  mem?: number;
  h?: number;
  th?: number;
  ior?: number;
  iow?: number;
  sus?: boolean;
}

interface FlatRow {
  proc: Proc;
  depth: number;
  hasChildren: boolean;
  collapsed: boolean;
}

interface ProcessStartResult {
  stdout?: string;
  stderr?: string;
  exitCode?: number;
  timedOut?: boolean;
  durationMs?: number;
  processId?: number;
  elevated?: boolean;
}

interface HandleRow {
  id: string;
  type: string;
  name?: string | null;
}

const SYSTEM_CRITICAL = new Set([
  "lsass",
  "winlogon",
  "csrss",
  "smss",
  "wininit",
  "services",
  "System",
  "Registry",
  "Idle",
  "svchost",
  "MemCompression",
]);

function cpuColor(pct: number): string {
  if (pct > 50) return "var(--danger, #dc2626)";
  if (pct > 20) return "#f59e0b";
  return "var(--ok, #16a34a)";
}

function splitProcessArguments(input: string): string[] {
  const args: string[] = [];
  let cur = "";
  let quoted = false;

  for (let i = 0; i < input.length; i++) {
    const ch = input[i];
    if (ch === "\\" && input[i + 1] === '"') {
      cur += '"';
      i++;
      continue;
    }
    if (ch === '"') {
      quoted = !quoted;
      continue;
    }
    if (/\s/.test(ch) && !quoted) {
      if (cur.length > 0) {
        args.push(cur);
        cur = "";
      }
      continue;
    }
    cur += ch;
  }

  if (cur.length > 0) args.push(cur);
  return args;
}

/** Sparkline SVG compacte à partir d'une série de valeurs normalisées 0..max. */
function sparkline(values: number[], max: number, color: string, w = 64, h = 18): string {
  if (values.length < 2) return "";
  const m = max > 0 ? max : 1;
  const step = w / (HIST_LEN - 1);
  const pts = values
    .map((v, i) => {
      const x = (i + (HIST_LEN - values.length)) * step;
      const y = h - Math.min(h, (Math.max(0, v) / m) * h);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return `<polyline points="${pts}" fill="none" stroke="${color}" stroke-width="1.5"/>`;
}

type ProcType = "system" | "service" | "user";

const PROC_TYPE_FILTERS: Array<{ key: ProcType; label: string }> = [
  { key: "system", label: "Système" },
  { key: "service", label: "Service" },
  { key: "user", label: "Utilisateur" },
];

const ProcessPanel: Component = () => {
  const ws = useWs();
  const client = useToolClient();
  const confirm = useConfirm();
  const enabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;

  const [procs, setProcs] = createSignal<Map<number, Proc>>(new Map());
  const [status, setStatus] = createSignal<PanelStatus | undefined>();
  const [intervalMs, setIntervalMs] = createSignal(5000);
  const [search, setSearch] = createSignal("");
  const [collapsed, setCollapsed] = createSignal<Set<number>>(new Set());
  const [selected, setSelected] = createSignal<number | null>(null);
  const [sortKey, setSortKey] = createSignal<"cpu" | "mem" | "name" | "pid" | "handles">("cpu");
  const [globalCpu, setGlobalCpu] = createSignal<number[]>([]);
  const [globalMem, setGlobalMem] = createSignal<number[]>([]);
  const [cpuCores, setCpuCores] = createSignal(1);
  const [live, setLive] = createSignal(false);
  const [videoActive, setVideoActive] = createSignal(false);
  // Timestamp du dernier push WS reçu — alimenté par onJobEvent (cf. plus bas).
  const [lastWsUpdate, setLastWsUpdate] = createSignal<number | null>(null);

  // Indicateur de fraîcheur live/stale pour la toolbar : se base sur le
  // signal `live` (abonnement watch actif côté agent) + `lastWsUpdate`
  // (bumpé à chaque snapshot/delta). Considère "stale" si > 2×intervalMs.
  const refresh = usePanelRefresh({
    liveSignal: {
      active: live,
      lastUpdate: lastWsUpdate,
      get staleAfterMs() {
        return Math.max(8_000, intervalMs() * 2);
      },
    },
  });
  // Filtres par catégorie de processus (toutes actives au départ).
  const [typeFilter, setTypeFilter] = createSignal<Set<ProcType>>(new Set(["system", "service", "user"]));
  // true tant que le 1er message WS « snap » n'est pas arrivé → skeleton.
  const [loadedOnce, setLoadedOnce] = createSignal(false);
  // PID survolé : gèle le ré-ordonnancement temps réel pour éviter le « saut ».
  const [hoveredPid, setHoveredPid] = createSignal<number | null>(null);
  // PID ciblé par le menu contextuel ouvert.
  const [menuPid, setMenuPid] = createSignal<number | null>(null);
  const [launcherOpen, setLauncherOpen] = createSignal(false);
  const [startFileName, setStartFileName] = createSignal("");
  const [startArguments, setStartArguments] = createSignal("");
  const [startWorkingDirectory, setStartWorkingDirectory] = createSignal("");
  const [startCredentialRef, setStartCredentialRef] = createSignal("");
  const [startElevated, setStartElevated] = createSignal(false);
  const [startTimeoutMs, setStartTimeoutMs] = createSignal(120_000);
  const [startingProcess, setStartingProcess] = createSignal(false);
  const [lastStartResult, setLastStartResult] = createSignal<ProcessStartResult | null>(null);

  // Menu contextuel clic droit.
  const { menu: ctxMenu, openAt: openCtxMenu, close: closeCtxMenu } = createContextMenu();

  let jobId: string | null = null;
  let pingTimer: ReturnType<typeof setInterval> | null = null;
  let lastPingAt = 0;
  let pausedForVideo = false;
  let offWs: (() => void) | null = null;
  let watchStarting = false;
  let watchStarted = false;
  let watchStartTimer: ReturnType<typeof setTimeout> | null = null;

  function toggleTypeFilter(t: ProcType) {
    setTypeFilter((s) => {
      const n = new Set(s);
      if (n.has(t)) n.delete(t);
      else n.add(t);
      return n;
    });
  }

  // Le rafraîchissement est gelé quand une ligne est survolée ou qu'un menu
  // contextuel est ouvert → la ligne ciblée ne « saute » pas sous le curseur.
  const isFrozen = () => hoveredPid() != null || ctxMenu.show();

  // ─── Application du snapshot / des deltas ───────────────────────────────
  function rawToProc(r: RawProc, prev?: Proc): Proc {
    return {
      pid: r.p,
      ppid: r.pp ?? null,
      name: r.n ?? prev?.name ?? "?",
      path: r.path ?? prev?.path ?? null,
      user: r.u ?? prev?.user ?? null,
      desc: r.d ?? prev?.desc ?? null,
      type: r.ty ?? prev?.type ?? "user",
      start: r.st ?? prev?.start ?? null,
      cmd: r.cmd ?? prev?.cmd ?? null,
      cpu: r.cpu ?? 0,
      mem: r.mem ?? prev?.mem ?? 0,
      handles: r.h ?? prev?.handles ?? 0,
      threads: r.th ?? prev?.threads ?? 0,
      ioR: r.ior ?? 0,
      ioW: r.iow ?? 0,
      suspended: r.sus ?? prev?.suspended ?? false,
      cpuHist: prev?.cpuHist ?? [],
      memHist: prev?.memHist ?? [],
    };
  }

  function pushHist(p: Proc) {
    p.cpuHist = [...p.cpuHist, p.cpu].slice(-HIST_LEN);
    p.memHist = [...p.memHist, p.mem].slice(-HIST_LEN);
  }

  function applySnapshot(list: RawProc[], cores?: number) {
    const map = new Map<number, Proc>();
    for (const r of list) {
      const p = rawToProc(r);
      pushHist(p);
      map.set(p.pid, p);
    }
    if (cores && cores > 0) setCpuCores(cores);
    setProcs(map);
    updateGlobal(map);
    setLoadedOnce(true);
  }

  function applyDelta(add: RawProc[], upd: RawProc[], rm: number[], cores?: number) {
    const map = new Map(procs());
    for (const pid of rm) map.delete(pid);
    for (const r of add) {
      const p = rawToProc(r);
      pushHist(p);
      map.set(p.pid, p);
    }
    for (const r of upd) {
      const prev = map.get(r.p);
      if (!prev) continue;
      const p = rawToProc(r, prev);
      pushHist(p);
      map.set(p.pid, p);
    }
    if (cores && cores > 0) setCpuCores(cores);
    setProcs(map);
    updateGlobal(map);
  }

  function updateGlobal(map: Map<number, Proc>) {
    let cpu = 0;
    let mem = 0;
    for (const p of map.values()) {
      cpu += p.cpu;
      mem += p.mem;
    }
    const maxCpu = cpuCores() * 100;
    setGlobalCpu((h) => [...h, Math.min(maxCpu, cpu)].slice(-GLOBAL_HIST));
    setGlobalMem((h) => [...h, mem].slice(-GLOBAL_HIST));
  }

  function stopPing() {
    if (pingTimer) clearInterval(pingTimer);
    pingTimer = null;
  }

  function startPing() {
    stopPing();
    pingTimer = setInterval(() => {
      const jid = jobId;
      if (!jid || !live()) return;
      const now = Date.now();
      if (videoActive() && now - lastPingAt < 30_000) return;
      lastPingAt = now;
      void client.call("process_watch_ping", { jobId: jid }, { timeoutMs: 5_000 }).catch(() => {
        // Best-effort heartbeat: le TTL côté agent libère les abonnements
        // orphelins si l'onglet disparaît avant process_watch_stop.
      });
    }, 8_000);
  }

  // ─── Démarrage / arrêt du suivi ─────────────────────────────────────────
  async function startWatch(retried = false) {
    if (live() || watchStarting || watchStarted || !enabled()) return;
    watchStarting = true;
    setStatus({ text: "Démarrage du suivi…", level: "info" });
    try {
      const r = (await client.call("process_watch_start", { intervalMs: intervalMs() }, { timeoutMs: 20_000 })) as {
        jobId: string;
        intervalMs: number;
      };
      jobId = r.jobId;
      const onJobEvent = (m: unknown) => {
        const msg = m as { op?: string; payload?: Record<string, unknown> };
        const pl = msg.payload ?? {};
        if (msg.op === "watch.snapshot") {
          applySnapshot((pl.procs as RawProc[]) ?? [], pl.cpuCores as number | undefined);
          setLastWsUpdate(Date.now());
        } else if (msg.op === "watch.delta") {
          applyDelta(
            (pl.add as RawProc[]) ?? [],
            (pl.upd as RawProc[]) ?? [],
            (pl.rm as number[]) ?? [],
            pl.cpuCores as number | undefined,
          );
          setLastWsUpdate(Date.now());
        } else if (msg.op === "watch.error")
          setStatus({ text: `Agent : ${String(pl.e ?? pl.error ?? pl.message ?? "")}`, level: "danger" });
        else if (msg.op === "watch.stopped") {
          setLive(false);
          watchStarted = false;
        }
      };
      offWs?.();
      offWs = ws.on(
        (m) =>
          isEnvelope(m) &&
          m.family === "live" &&
          m.type === "process" &&
          m.op.startsWith("watch.") &&
          m.payload?.jobId === jobId,
        onJobEvent,
      );
      watchStarting = false;
      watchStarted = true;
      setLive(true);
      startPing();
      setStatus({ text: `Suivi actif (${r.intervalMs / 1000}s)`, level: "info" });
    } catch (e) {
      watchStarting = false;
      const msg = e instanceof Error ? e.message : String(e);
      // Suivi orphelin côté agent (panel rechargé, relais redémarré…). On force
      // l'arrêt puis on retente UNE SEULE FOIS. `stopWatch()` est court-circuité
      // par son garde `live()` (faux ici, le start a échoué) — on appelle donc
      // directement `process_watch_stop`. Le flag `retried` évite la boucle
      // infinie de `process_watch_start` qui saturait l'agent (CPU).
      if ((msg.includes("déjà actif") || msg.includes("already active")) && !retried) {
        setStatus({ text: "Suivi existant détecté — redémarrage…", level: "warn" });
        try {
          await client.call("process_watch_stop", jobId ? { jobId } : {}, { timeoutMs: 10_000 });
        } catch {
          /* le plugin s'arrête aussi de lui-même */
        }
        await startWatch(true);
        return;
      }
      setStatus({ text: `Échec : ${msg}`, level: "danger" });
    }
  }

  function stopWatch(silent = false) {
    if (watchStartTimer) {
      clearTimeout(watchStartTimer);
      watchStartTimer = null;
    }
    if (!live() && !watchStarting && !watchStarted) return;
    watchStarted = false;
    watchStarting = false;
    setLive(false);
    stopPing();
    offWs?.();
    offWs = null;
    const jid = jobId;
    jobId = null;
    void (async () => {
      try {
        await client.call("process_watch_stop", jid ? { jobId: jid } : {}, { timeoutMs: 10_000 });
      } catch {
        /* le plugin s'arrête aussi de lui-même */
      }
    })();
    if (!silent) setStatus({ text: "Suivi arrêté", level: "info" });
  }

  onMount(() => {
    const onVideoActive = (event: Event) => {
      const detail = (event as CustomEvent<{ active?: boolean }>).detail;
      setVideoActive(detail?.active === true);
    };
    window.addEventListener("remotews:video-active", onVideoActive);
    onCleanup(() => window.removeEventListener("remotews:video-active", onVideoActive));
  });

  // Démarre le watch une seule fois quand les conditions sont réunies, avec un
  // léger debounce pour ne pas saturer l'agent si enabled() flappe (consentement,
  // reconnexion, etc.). Le watch n'est pas arrêté automatiquement sur un flicker
  // de enabled() : seul le démontage du panel ou la vidéo active l'arrête.
  createEffect(() => {
    const shouldStart = enabled() && !videoActive() && !watchStarted && !watchStarting;
    if (!shouldStart) return;
    if (watchStartTimer) clearTimeout(watchStartTimer);
    watchStartTimer = setTimeout(() => void startWatch(), 300);
  });

  createEffect(() => {
    if (videoActive() && live()) {
      pausedForVideo = true;
      stopWatch(true);
      setStatus({ text: "Suivi process suspendu pendant la prise en main", level: "info" });
      return;
    }
    if (!videoActive() && pausedForVideo) {
      pausedForVideo = false;
      // Le createEffect enabled ci-dessus se chargera de redémarrer.
    }
  });
  onCleanup(() => {
    stopWatch(true);
    stopPing();
    offWs?.();
  });

  // ─── Arbre ───────────────────────────────────────────────────────────────
  const sortCmp = (a: Proc, b: Proc): number => {
    switch (sortKey()) {
      case "mem":
        return b.mem - a.mem;
      case "handles":
        return b.handles - a.handles;
      case "name":
        return a.name.localeCompare(b.name);
      case "pid":
        return a.pid - b.pid;
      default:
        return b.cpu - a.cpu;
    }
  };

  // Liste à plat ordonnée : arbre hiérarchique si pas de recherche, sinon
  // liste filtrée plate (les matchs perdraient leur contexte dans l'arbre).
  const computeRows = (): FlatRow[] => {
    const map = procs();
    const q = search().trim().toLowerCase();
    const types = typeFilter();
    const visible = (p: Proc) => types.has((p.type as ProcType) ?? "user");

    if (q) {
      const flat = [...map.values()]
        .filter(
          (p) =>
            visible(p) &&
            (p.name.toLowerCase().includes(q) ||
              String(p.pid).includes(q) ||
              (p.user ?? "").toLowerCase().includes(q) ||
              (p.desc ?? "").toLowerCase().includes(q)),
        )
        .sort(sortCmp);
      return flat.map((proc) => ({ proc, depth: 0, hasChildren: false, collapsed: false }));
    }

    // Index enfants par ppid (en ne gardant que les types visibles).
    const children = new Map<number, Proc[]>();
    for (const p of map.values()) {
      if (!visible(p)) continue;
      const pp = p.ppid != null && map.has(p.ppid) ? p.ppid : -1;
      (children.get(pp) ?? children.set(pp, []).get(pp)!).push(p);
    }
    for (const list of children.values()) list.sort(sortCmp);

    const coll = collapsed();
    const out: FlatRow[] = [];
    const walk = (pid: number, depth: number) => {
      const kids = children.get(pid) ?? [];
      for (const proc of kids) {
        const sub = children.get(proc.pid) ?? [];
        const isColl = coll.has(proc.pid);
        out.push({ proc, depth, hasChildren: sub.length > 0, collapsed: isColl });
        if (!isColl) walk(proc.pid, depth + 1);
      }
    };
    walk(-1, 0);
    return out;
  };

  // Snapshot des lignes : gelé tant que l'utilisateur survole une ligne ou
  // qu'un menu contextuel est ouvert, pour éviter que la ligne ciblée saute.
  let frozenRows: FlatRow[] = [];
  const rows = createMemo((): FlatRow[] => {
    if (isFrozen()) return frozenRows;
    frozenRows = computeRows();
    return frozenRows;
  });

  function toggleCollapse(pid: number) {
    setCollapsed((s) => {
      const n = new Set(s);
      if (n.has(pid)) n.delete(pid);
      else n.add(pid);
      return n;
    });
  }

  const selectedProc = createMemo(() => {
    const pid = selected();
    return pid == null ? null : (procs().get(pid) ?? null);
  });

  function isCritical(p: Proc): boolean {
    return SYSTEM_CRITICAL.has(p.name);
  }

  // ─── Actions ─────────────────────────────────────────────────────────────
  async function doAction(p: Proc, tool: string, label: string, danger: boolean, extra: Record<string, unknown> = {}) {
    if (danger) {
      const ok = await confirm.confirm({
        title: `${label} — ${p.name} (PID ${p.pid})`,
        body: `Chemin : ${p.path ?? "inconnu"}`,
        level: "danger",
        confirmLabel: label,
        entityName: p.name,
      });
      if (!ok) return;
    }
    setStatus({ text: `${label} PID ${p.pid}…`, level: "warn" });
    try {
      await client.call(tool, { pid: p.pid, ...extra }, { userConfirmedAt: new Date().toISOString() });
      setStatus({ text: `${label} : OK`, level: "info" });
    } catch (e) {
      setStatus({ text: `${label} : ${e instanceof Error ? e.message : String(e)}`, level: "danger" });
    }
  }

  async function doProcDump(p: Proc) {
    const ok = await confirm.confirm({
      title: `ProcDump — ${p.name} (PID ${p.pid})`,
      body: "Générer un dump mémoire complet avec ProcDump (Sysinternals). Le fichier sera stocké côté host.",
      level: "warn",
      confirmLabel: "Générer",
      entityName: p.name,
    });
    if (!ok) return;
    setStatus({ text: `ProcDump PID ${p.pid}…`, level: "warn" });
    try {
      const r = (await client.call(
        "process_procdump",
        { pid: p.pid },
        { timeoutMs: 120_000, userConfirmedAt: new Date().toISOString() },
      )) as { dumpPath?: string; dumpFile?: string; processId?: number };
      setStatus({ text: `Dump créé : ${r.dumpPath ?? r.dumpFile ?? "?"}`, level: "info" });
    } catch (e) {
      setStatus({ text: `ProcDump : ${e instanceof Error ? e.message : String(e)}`, level: "danger" });
    }
  }

  async function startProcess() {
    const fileName = startFileName().trim();
    const credentialRef = startCredentialRef().trim();
    if (!fileName) {
      setStatus({ text: "Chemin exécutable requis", level: "danger" });
      return;
    }
    if (!credentialRef) {
      setStatus({ text: "credentialRef requis", level: "danger" });
      return;
    }

    const ok = await confirm.confirm({
      title: `Lancer — ${fileName.split(/[\\/]/).pop() || fileName}`,
      body: startElevated() ? "Exécution avec token haute intégrité." : "Exécution avec credentialRef stocké.",
      level: startElevated() ? "danger" : "warn",
      confirmLabel: "Lancer",
    });
    if (!ok) return;

    setStartingProcess(true);
    setLastStartResult(null);
    setStatus({ text: "Lancement du processus…", level: "warn" });
    try {
      const timeoutMs = Math.max(1_000, Math.min(24 * 60 * 60 * 1000, startTimeoutMs()));
      const result = (await client.call(
        "process_start",
        {
          fileName,
          arguments: splitProcessArguments(startArguments()),
          workingDirectory: startWorkingDirectory().trim() || undefined,
          credentialRef,
          elevated: startElevated(),
          timeoutMs,
        },
        { timeoutMs: timeoutMs + 10_000, userConfirmedAt: new Date().toISOString() },
      )) as ProcessStartResult;
      setLastStartResult(result);
      setStatus({
        text: `Processus terminé code ${result.exitCode ?? "?"}${result.timedOut ? " (timeout)" : ""}`,
        level: result.exitCode === 0 && !result.timedOut ? "info" : "warn",
      });
    } catch (e) {
      setStatus({ text: `Lancement : ${e instanceof Error ? e.message : String(e)}`, level: "danger" });
    } finally {
      setStartingProcess(false);
    }
  }

  // ─── Menu contextuel (clic droit sur une ligne) ─────────────────────────
  // L'ouverture mémorise le PID ciblé ; la fermeture le réinitialise.
  function openRowMenu(p: Proc, e: MouseEvent) {
    setMenuPid(p.pid);
    openCtxMenu(e);
  }
  function closeRowMenu() {
    closeCtxMenu();
    setMenuPid(null);
  }

  // Items dérivés du process ciblé. Seules les actions disposant d'un outil
  // backend sont exposées (cf. manifest plugin process).
  const ctxItems = createMemo((): ContextMenuItem[] => {
    const pid = menuPid();
    const p = pid == null ? null : (procs().get(pid) ?? null);
    if (!p) return [];
    const critical = isCritical(p);
    const items: ContextMenuItem[] = [];

    if (p.suspended) {
      items.push({ label: "Reprendre", onSelect: () => void doAction(p, "process_resume", "Reprendre", false) });
    } else {
      items.push({
        label: "Suspendre",
        disabled: critical,
        onSelect: () => void doAction(p, "process_suspend", "Suspendre", true),
      });
    }
    items.push({ label: "Définir priorité…", onSelect: () => void promptPriority(p) });
    items.push({
      label: "Handles…",
      onSelect: () => {
        openDetail(p.pid);
        setDetailTab("handles");
        void loadHandles();
      },
    });
    items.push({ separator: true });
    items.push({
      label: "Terminer",
      danger: true,
      disabled: critical,
      onSelect: () => void doAction(p, "process_kill", "Terminer", true),
    });
    // process_kill { force:true } → entireProcessTree côté plugin.
    items.push({
      label: "Tuer l'arbre de processus",
      danger: true,
      disabled: critical,
      onSelect: () => void doAction(p, "process_kill", "Tuer l'arbre de processus", true, { force: true }),
    });
    items.push({ separator: true });
    items.push({
      label: "ProcDump",
      onSelect: () => void doProcDump(p),
    });
    return items;
  });

  // Choix de priorité hors panneau de détail : second menu contextuel.
  const PRIORITIES: Array<{ value: string; label: string }> = [
    { value: "idle", label: "Idle" },
    { value: "belownormal", label: "Below normal" },
    { value: "normal", label: "Normal" },
    { value: "abovenormal", label: "Above normal" },
    { value: "high", label: "High" },
    { value: "realtime", label: "Realtime" },
  ];
  const { menu: prioMenu, openAt: openPrioMenu, close: closePrioMenu } = createContextMenu();
  const [prioTarget, setPrioTarget] = createSignal<Proc | null>(null);

  function promptPriority(p: Proc) {
    setPrioTarget(p);
    // Réouvre un menu à la même position que le menu contextuel d'origine.
    openPrioMenu({ clientX: ctxMenu.x(), clientY: ctxMenu.y() });
  }

  const prioItems = createMemo((): ContextMenuItem[] => {
    const p = prioTarget();
    if (!p) return [];
    return PRIORITIES.map((pr) => ({
      label: pr.label,
      onSelect: () => void doAction(p, "process_set_priority", `Priorité ${pr.label}`, true, { priority: pr.value }),
    }));
  });

  // ─── Détail : threads / modules à la demande ────────────────────────────
  const [detailTab, setDetailTab] = createSignal<"general" | "threads" | "modules" | "handles">("general");
  const [threads, setThreads] = createSignal<unknown[]>([]);
  const [modules, setModules] = createSignal<unknown[]>([]);
  const [handles, setHandles] = createSignal<HandleRow[]>([]);
  const [handlesLoading, setHandlesLoading] = createSignal(false);

  async function loadDetail(tab: "threads" | "modules") {
    const p = selectedProc();
    if (!p) return;
    try {
      if (tab === "threads") {
        const r = (await client.call("process_threads", { pid: p.pid })) as { threads: unknown[] };
        setThreads(r.threads ?? []);
      } else {
        const r = (await client.call("process_modules", { pid: p.pid })) as { modules: unknown[] };
        setModules(r.modules ?? []);
      }
    } catch (e) {
      setStatus({ text: `Détail : ${e instanceof Error ? e.message : String(e)}`, level: "danger" });
    }
  }

  async function loadHandles() {
    const p = selectedProc();
    if (!p) return;
    setHandlesLoading(true);
    try {
      const r = (await client.call("process_handles_list", { pid: p.pid, limit: 500 })) as {
        handles?: unknown[];
      };
      const rows = (r.handles ?? []).map((h) => {
        const row = (h ?? {}) as Record<string, unknown>;
        return {
          id: String(row.id ?? row.Id ?? ""),
          type: String(row.type ?? row.Type ?? ""),
          name: row.name ?? row.Name ?? null,
        } as HandleRow;
      });
      setHandles(rows);
    } catch (e) {
      setStatus({ text: `Handles : ${e instanceof Error ? e.message : String(e)}`, level: "danger" });
      setHandles([]);
    } finally {
      setHandlesLoading(false);
    }
  }

  async function closeHandle(row: HandleRow) {
    const p = selectedProc();
    if (!p || !row.id) return;
    const ok = await confirm.confirm({
      title: `Fermer handle ${row.id}`,
      body: `Processus ${p.name} (PID ${p.pid}) — type ${row.type || "?"}. Cette action peut faire planter le processus cible.`,
      level: "danger",
      confirmLabel: "Fermer",
      entityName: p.name,
    });
    if (!ok) return;
    setStatus({ text: `Fermeture handle ${row.id}…`, level: "warn" });
    try {
      await client.call(
        "process_close_handle",
        { pid: p.pid, handleId: row.id },
        { timeoutMs: 30_000, userConfirmedAt: new Date().toISOString() },
      );
      setStatus({ text: `Handle ${row.id} fermé`, level: "info" });
      await loadHandles();
    } catch (e) {
      setStatus({ text: `Fermeture handle : ${e instanceof Error ? e.message : String(e)}`, level: "danger" });
    }
  }

  function openDetail(pid: number) {
    setSelected(pid);
    setDetailTab("general");
    setThreads([]);
    setModules([]);
  }

  // ─── Rendu ───────────────────────────────────────────────────────────────
  const globalCpuNow = () => globalCpu()[globalCpu().length - 1] ?? 0;
  const globalMemNow = () => globalMem()[globalMem().length - 1] ?? 0;
  const maxMem = () => Math.max(1, ...globalMem());

  const actions = (
    <>
      <input
        class="panel-filter-input"
        placeholder="Filtrer nom / PID / user…"
        value={search()}
        onInput={(e) => setSearch(e.currentTarget.value)}
      />
      <div class="proc-filter-pills" role="group" aria-label="Filtrer par catégorie">
        <For each={PROC_TYPE_FILTERS}>
          {(f) => (
            <button
              type="button"
              class={`proc-pill proc-pill--${f.key}`}
              classList={{ "proc-pill--active": typeFilter().has(f.key) }}
              aria-pressed={typeFilter().has(f.key)}
              title={`${typeFilter().has(f.key) ? "Masquer" : "Afficher"} les processus ${f.label}`}
              onClick={() => toggleTypeFilter(f.key)}
            >
              <span class="proc-pill-dot" />
              {f.label}
            </button>
          )}
        </For>
      </div>
      <select
        class="proc-interval"
        value={String(intervalMs())}
        onChange={(e) => setIntervalMs(Number(e.currentTarget.value))}
        title="Cadence de rafraîchissement"
      >
        <option value="2000">2s</option>
        <option value="5000">5s</option>
        <option value="10000">10s</option>
        <option value="30000">30s</option>
      </select>
      <button
        class={`btn btn--xs ${live() ? "btn--ok" : "btn--ghost"}`}
        onClick={() => (live() ? void stopWatch() : void startWatch())}
      >
        {live() ? "● Live" : "○ Live"}
      </button>
      <RefreshIndicator state={refresh} />
      <button
        class="btn btn--xs"
        classList={{ "btn--primary": launcherOpen() }}
        onClick={() => setLauncherOpen((v) => !v)}
        title="Lancer un processus avec credentialRef"
      >
        + Lancer
      </button>
      <span class="proc-count">{procs().size} proc.</span>
    </>
  );

  return (
    <PanelChrome icon="⚙" title="Processus" tagType="core" actions={actions}>
      <PanelLayout title="Processus" status={status()}>
        <div class="proc-panel">
          <Show when={launcherOpen()}>
            <form
              class="proc-launcher"
              onSubmit={(e) => {
                e.preventDefault();
                void startProcess();
              }}
            >
              <div class="proc-launcher-grid">
                <label class="proc-launcher-field proc-launcher-field--wide">
                  <span>Exécutable</span>
                  <input
                    value={startFileName()}
                    onInput={(e) => setStartFileName(e.currentTarget.value)}
                    placeholder="C:\\Windows\\System32\\WindowsPowerShell\\v1.0\\powershell.exe"
                    autocomplete="off"
                    spellcheck={false}
                  />
                </label>
                <label class="proc-launcher-field">
                  <span>credentialRef</span>
                  <input
                    value={startCredentialRef()}
                    onInput={(e) => setStartCredentialRef(e.currentTarget.value)}
                    placeholder="cred:windows:admin"
                    autocomplete="off"
                    spellcheck={false}
                  />
                </label>
                <label class="proc-launcher-field proc-launcher-field--wide">
                  <span>Arguments</span>
                  <input
                    value={startArguments()}
                    onInput={(e) => setStartArguments(e.currentTarget.value)}
                    placeholder='-NoProfile -Command "whoami"'
                    autocomplete="off"
                    spellcheck={false}
                  />
                </label>
                <label class="proc-launcher-field">
                  <span>Dossier</span>
                  <input
                    value={startWorkingDirectory()}
                    onInput={(e) => setStartWorkingDirectory(e.currentTarget.value)}
                    placeholder="C:\\Windows"
                    autocomplete="off"
                    spellcheck={false}
                  />
                </label>
                <label class="proc-launcher-field proc-launcher-field--small">
                  <span>Timeout</span>
                  <input
                    type="number"
                    min="1000"
                    step="1000"
                    value={String(startTimeoutMs())}
                    onInput={(e) => setStartTimeoutMs(Number(e.currentTarget.value) || 120_000)}
                  />
                </label>
                <label class="proc-launcher-check" title="Utiliser le token UAC haute intégrité si disponible">
                  <input
                    type="checkbox"
                    checked={startElevated()}
                    onChange={(e) => setStartElevated(e.currentTarget.checked)}
                  />
                  Élevé
                </label>
              </div>
              <div class="proc-launcher-actions">
                <button
                  type="button"
                  class="btn btn--xs btn--ghost"
                  onClick={() => {
                    setLauncherOpen(false);
                    setLastStartResult(null);
                  }}
                >
                  Fermer
                </button>
                <button
                  type="submit"
                  class="btn btn--xs btn--primary"
                  disabled={startingProcess() || !startFileName().trim() || !startCredentialRef().trim()}
                >
                  {startingProcess() ? "Lancement…" : "Lancer"}
                </button>
              </div>
              <Show when={lastStartResult()}>
                {(r) => (
                  <div class="proc-launcher-result" data-error={r().exitCode !== 0 || r().timedOut || undefined}>
                    <span>
                      code {r().exitCode ?? "?"}
                      {r().timedOut ? " · timeout" : ""}
                      {r().processId ? ` · PID ${r().processId}` : ""}
                    </span>
                    <Show when={r().stdout || r().stderr}>
                      <pre>{r().stdout || r().stderr}</pre>
                    </Show>
                  </div>
                )}
              </Show>
            </form>
          </Show>

          {/* Graphe global CPU + RAM */}
          <div class="proc-graphs">
            <div class="proc-graph">
              <div class="proc-graph-label">
                CPU <strong>{globalCpuNow().toFixed(0)}%</strong>
              </div>
              <svg viewBox="0 0 200 40" preserveAspectRatio="none" class="proc-graph-svg">
                <Show when={globalCpu().length > 1}>
                  <polyline
                    points={globalCpu()
                      .map((v, i) => `${(i / (GLOBAL_HIST - 1)) * 200},${40 - (v / (cpuCores() * 100)) * 40}`)
                      .join(" ")}
                    fill="none"
                    stroke="var(--danger,#dc2626)"
                    stroke-width="1.5"
                  />
                </Show>
              </svg>
            </div>
            <div class="proc-graph">
              <div class="proc-graph-label">
                RAM <strong>{formatBytes(globalMemNow())}</strong>
              </div>
              <svg viewBox="0 0 200 40" preserveAspectRatio="none" class="proc-graph-svg">
                <Show when={globalMem().length > 1}>
                  <polyline
                    points={globalMem()
                      .map((v, i) => `${(i / (GLOBAL_HIST - 1)) * 200},${40 - (v / maxMem()) * 40}`)
                      .join(" ")}
                    fill="none"
                    stroke="#2563eb"
                    stroke-width="1.5"
                  />
                </Show>
              </svg>
            </div>
          </div>

          <div class="proc-split">
            {/* Arbre des processus */}
            <div class="proc-tree-wrap">
              <table class="proc-tree">
                <thead>
                  <tr>
                    <th class="proc-col-name" onClick={() => setSortKey("name")}>
                      Nom
                    </th>
                    <th class="proc-col-pid" onClick={() => setSortKey("pid")}>
                      PID
                    </th>
                    <th class="proc-col-cpu" onClick={() => setSortKey("cpu")}>
                      CPU
                    </th>
                    <th class="proc-col-spark">Tendance</th>
                    <th class="proc-col-mem" onClick={() => setSortKey("mem")}>
                      RAM
                    </th>
                    <th class="proc-col-handles" onClick={() => setSortKey("handles")}>
                      Handles
                    </th>
                    <th class="proc-col-io">E/S disque</th>
                    <th class="proc-col-user">Utilisateur</th>
                    <th class="proc-col-desc">Description</th>
                  </tr>
                </thead>
                <tbody>
                  <Show
                    when={loadedOnce()}
                    fallback={
                      <tr>
                        <td colspan="9" class="proc-skeleton-cell">
                          <Show
                            when={live() || enabled()}
                            fallback={<div class="proc-empty">Suivi inactif — cliquer ● Live</div>}
                          >
                            <Skeleton variant="table" rows={12} />
                          </Show>
                        </td>
                      </tr>
                    }
                  >
                    <Show
                      when={rows().length > 0}
                      fallback={
                        <tr>
                          <td colspan="9" class="proc-empty">
                            {live() ? "Aucun processus ne correspond aux filtres." : "Suivi inactif — cliquer ● Live"}
                          </td>
                        </tr>
                      }
                    >
                      <For each={rows()}>
                        {(row) => {
                          const p = row.proc;
                          return (
                            <tr
                              class={`proc-row proc-ty-${p.type}`}
                              classList={{
                                "proc-selected": selected() === p.pid,
                                "proc-suspended": p.suspended,
                                "proc-row--menu": menuPid() === p.pid && ctxMenu.show(),
                              }}
                              onClick={() => openDetail(p.pid)}
                              onContextMenu={(e) => openRowMenu(p, e)}
                              onMouseEnter={() => setHoveredPid(p.pid)}
                              onMouseLeave={() => setHoveredPid((cur) => (cur === p.pid ? null : cur))}
                            >
                              <td class="proc-col-name">
                                <span style={{ "padding-left": `${row.depth * 14}px` }} class="proc-name-cell">
                                  <Show when={row.hasChildren} fallback={<span class="proc-twisty-spacer" />}>
                                    <span
                                      class="proc-twisty"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleCollapse(p.pid);
                                      }}
                                    >
                                      {row.collapsed ? "▸" : "▾"}
                                    </span>
                                  </Show>
                                  <span class="proc-name">{p.name}</span>
                                </span>
                              </td>
                              <td class="proc-col-pid">{p.pid}</td>
                              <td class="proc-col-cpu">
                                <div class="proc-cpu-bar">
                                  <div
                                    class="proc-cpu-fill"
                                    style={{
                                      width: `${Math.min(100, p.cpu)}%`,
                                      background: cpuColor(p.cpu),
                                    }}
                                  />
                                  <span class="proc-cpu-label">{p.cpu > 0 ? `${p.cpu.toFixed(1)}` : "—"}</span>
                                </div>
                              </td>
                              <td class="proc-col-spark">
                                <svg
                                  viewBox="0 0 64 18"
                                  class="proc-spark"
                                  ref={(el) => {
                                    if (el) el.innerHTML = sparkline(p.cpuHist, 100, cpuColor(p.cpu));
                                  }}
                                />
                              </td>
                              <td class="proc-col-mem">{formatBytes(p.mem)}</td>
                              <td class="proc-col-handles">{p.handles > 0 ? p.handles.toLocaleString() : "—"}</td>
                              <td class="proc-col-io">
                                <Show when={p.ioR > 0 || p.ioW > 0} fallback={<span class="proc-dim">—</span>}>
                                  <span class="proc-io-r">↓{formatBytes(p.ioR)}</span>{" "}
                                  <span class="proc-io-w">↑{formatBytes(p.ioW)}</span>
                                </Show>
                              </td>
                              <td class="proc-col-user" title={p.user ?? ""}>
                                {(p.user ?? "").split("\\").pop() || "—"}
                              </td>
                              <td class="proc-col-desc" title={p.desc ?? ""}>
                                {p.desc ?? ""}
                              </td>
                            </tr>
                          );
                        }}
                      </For>
                    </Show>
                  </Show>
                </tbody>
              </table>
            </div>

            {/* Panneau de détail */}
            <Show when={selectedProc()}>
              {(proc) => (
                <aside class="proc-detail">
                  <div class="proc-detail-head">
                    <h3>{proc().name}</h3>
                    <code>PID {proc().pid}</code>
                    <button class="proc-detail-close" onClick={() => setSelected(null)}>
                      ✕
                    </button>
                  </div>

                  <div class="proc-detail-actions">
                    <Show
                      when={!proc().suspended}
                      fallback={
                        <button
                          class="btn btn--xs"
                          onClick={() => void doAction(proc(), "process_resume", "Reprendre", false)}
                        >
                          ▶ Reprendre
                        </button>
                      }
                    >
                      <button
                        class="btn btn--xs"
                        disabled={isCritical(proc())}
                        onClick={() => void doAction(proc(), "process_suspend", "Suspendre", true)}
                      >
                        ⏸ Suspendre
                      </button>
                    </Show>
                    <select
                      class="proc-prio"
                      title="Priorité"
                      onChange={(e) =>
                        void doAction(proc(), "process_set_priority", "Priorité", true, {
                          priority: e.currentTarget.value,
                        })
                      }
                    >
                      <option value="">Priorité…</option>
                      <option value="idle">Idle</option>
                      <option value="belownormal">Below normal</option>
                      <option value="normal">Normal</option>
                      <option value="abovenormal">Above normal</option>
                      <option value="high">High</option>
                      <option value="realtime">Realtime</option>
                    </select>
                    <button
                      class="btn btn--xs btn--danger"
                      disabled={isCritical(proc())}
                      title={isCritical(proc()) ? "Processus critique — protégé" : "Tuer"}
                      onClick={() => void doAction(proc(), "process_kill", "Tuer", true, { force: true })}
                    >
                      ✕ Tuer
                    </button>
                    <button
                      class="btn btn--xs"
                      title="Générer un dump avec ProcDump (Sysinternals)"
                      onClick={() => void doProcDump(proc())}
                    >
                      🗑 ProcDump
                    </button>
                  </div>

                  <div class="proc-tabs">
                    <button
                      classList={{ "proc-tab-active": detailTab() === "general" }}
                      onClick={() => setDetailTab("general")}
                    >
                      Général
                    </button>
                    <button
                      classList={{ "proc-tab-active": detailTab() === "threads" }}
                      onClick={() => {
                        setDetailTab("threads");
                        void loadDetail("threads");
                      }}
                    >
                      Threads ({proc().threads})
                    </button>
                    <button
                      classList={{ "proc-tab-active": detailTab() === "modules" }}
                      onClick={() => {
                        setDetailTab("modules");
                        void loadDetail("modules");
                      }}
                    >
                      Modules
                    </button>
                    <button
                      classList={{ "proc-tab-active": detailTab() === "handles" }}
                      onClick={() => {
                        setDetailTab("handles");
                        void loadHandles();
                      }}
                    >
                      Handles
                    </button>
                  </div>

                  <Show when={detailTab() === "general"}>
                    <dl class="proc-dl">
                      <dt>Type</dt>
                      <dd>{proc().type}</dd>
                      <dt>Utilisateur</dt>
                      <dd>{proc().user ?? "—"}</dd>
                      <dt>Démarré</dt>
                      <dd>{formatRelative(proc().start)}</dd>
                      <dt>CPU</dt>
                      <dd>{proc().cpu.toFixed(1)} %</dd>
                      <dt>RAM</dt>
                      <dd>{formatBytes(proc().mem)}</dd>
                      <dt>E/S disque</dt>
                      <dd>
                        ↓{formatBytes(proc().ioR)}/s ↑{formatBytes(proc().ioW)}/s
                      </dd>
                      <dt>Handles</dt>
                      <dd>{proc().handles}</dd>
                      <dt>Threads</dt>
                      <dd>{proc().threads}</dd>
                      <Show when={proc().path}>
                        <dt>Chemin</dt>
                        <dd class="proc-mono" title={proc().path!}>
                          {proc().path}
                        </dd>
                      </Show>
                      <Show when={proc().cmd}>
                        <dt>Cmd</dt>
                        <dd class="proc-mono" title={proc().cmd!}>
                          {proc().cmd}
                        </dd>
                      </Show>
                    </dl>
                  </Show>

                  <Show when={detailTab() === "threads"}>
                    <table class="proc-subtable">
                      <thead>
                        <tr>
                          <th>TID</th>
                          <th>État</th>
                          <th>Prio</th>
                          <th>CPU</th>
                        </tr>
                      </thead>
                      <tbody>
                        <For each={threads() as Array<Record<string, unknown>>}>
                          {(t) => (
                            <tr>
                              <td>{String(t.id)}</td>
                              <td>
                                {String(t.state)}
                                {t.wait ? ` (${t.wait})` : ""}
                              </td>
                              <td>{String(t.priority)}</td>
                              <td>{t.cpuSec ? `${t.cpuSec}s` : "—"}</td>
                            </tr>
                          )}
                        </For>
                      </tbody>
                    </table>
                  </Show>

                  <Show when={detailTab() === "modules"}>
                    <table class="proc-subtable">
                      <thead>
                        <tr>
                          <th>Module</th>
                          <th>Taille</th>
                          <th>Version</th>
                        </tr>
                      </thead>
                      <tbody>
                        <For each={modules() as Array<Record<string, unknown>>}>
                          {(m) => (
                            <tr>
                              <td title={String(m.path ?? "")}>{String(m.name)}</td>
                              <td>{formatBytes(Number(m.sizeBytes ?? 0))}</td>
                              <td>{String(m.version ?? "—")}</td>
                            </tr>
                          )}
                        </For>
                      </tbody>
                    </table>
                  </Show>

                  <Show when={detailTab() === "handles"}>
                    <div class="proc-detail-actions">
                      <button class="btn btn--xs" onClick={() => void loadHandles()} disabled={handlesLoading()}>
                        ↻ Rafraîchir
                      </button>
                    </div>
                    <Show when={!handlesLoading()} fallback={<div class="proc-empty">Chargement des handles…</div>}>
                      <table class="proc-subtable">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Type</th>
                            <th>Nom</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          <For
                            each={handles()}
                            fallback={
                              <tr>
                                <td colspan="4" class="proc-empty">
                                  Aucun handle trouvé.
                                </td>
                              </tr>
                            }
                          >
                            {(h) => (
                              <tr>
                                <td class="proc-mono">{h.id}</td>
                                <td>{h.type || "—"}</td>
                                <td class="proc-mono" title={h.name ?? undefined}>
                                  {h.name || "—"}
                                </td>
                                <td>
                                  <button class="btn btn--xs btn--danger" onClick={() => void closeHandle(h)}>
                                    Fermer
                                  </button>
                                </td>
                              </tr>
                            )}
                          </For>
                        </tbody>
                      </table>
                    </Show>
                  </Show>
                </aside>
              )}
            </Show>
          </div>
        </div>

        {/* Menu contextuel clic droit sur une ligne de processus */}
        <Show when={ctxMenu.show()}>
          <ContextMenu x={ctxMenu.x()} y={ctxMenu.y()} items={ctxItems()} onClose={closeRowMenu} />
        </Show>
        {/* Sous-menu de choix de priorité */}
        <Show when={prioMenu.show()}>
          <ContextMenu x={prioMenu.x()} y={prioMenu.y()} items={prioItems()} onClose={closePrioMenu} />
        </Show>
      </PanelLayout>
    </PanelChrome>
  );
};

registerPanel("process", ProcessPanel);
export default ProcessPanel;
